import { METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT, QueryKeys, Rfc9457Error } from "@cede/utils";
import { useHubUser } from "./useHubUser";
import { useApi } from "@/hooks/core/useApi";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const NicknameErrorCode = {
  TAKEN: "nickname_taken",
  ALREADY_SET: "nickname_already_set",
  INAPPROPRIATE: "nickname_inappropriate",
  UNKNOWN: "unknown",
} as const;
export type NicknameErrorCode = (typeof NicknameErrorCode)[keyof typeof NicknameErrorCode];

/**
 * Surface that triggered the nickname upsert. Sent as the `via` property
 * on `CEDE_ARENA_USERNAME_SET` so we can split Mixpanel by entry point.
 */
export type NicknameSource = "trade_flow";

// Footgun: `Rfc9457Error.message` is the human-readable `detail`, NOT the
// error code. The structured uppercase code lives at `err.details.type` —
// switch on that or the parser silently falls through to UNKNOWN.
export const parseNicknameError = (err: unknown): NicknameErrorCode => {
  if (!(err instanceof Rfc9457Error)) return NicknameErrorCode.UNKNOWN;
  switch (err.details.type) {
    case "NICKNAME_INAPPROPRIATE":
      return NicknameErrorCode.INAPPROPRIATE;
    case "NICKNAME_ALREADY_SET":
      return NicknameErrorCode.ALREADY_SET;
    case "NICKNAME_ALREADY_TAKEN":
      return NicknameErrorCode.TAKEN;
    default:
      return NicknameErrorCode.UNKNOWN;
  }
};

// The UNKNOWN branch logs the raw error so 5xx / network blips reach the console / Sentry
// instead of getting hidden behind the generic message.
export const getNicknameErrorMessage = (err: unknown): string => {
  const code = parseNicknameError(err);
  switch (code) {
    case NicknameErrorCode.ALREADY_SET:
      return "Names can only be set once. Contact support if this is a mistake.";
    case NicknameErrorCode.TAKEN:
      return "This name is already taken. Please choose another.";
    case NicknameErrorCode.INAPPROPRIATE:
      return "This name isn't allowed. Please choose another.";
    default:
      console.error("Nickname mutation failed with unknown error", err);
      return "Failed to set name. Please try again.";
  }
};

interface UpsertNicknameVariables {
  nickname: string;
  via: NicknameSource;
}

export const useNickname = () => {
  const apiHelper = useApi();
  const { hubUser, auth } = useHubUser();
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();

  const {
    mutateAsync: upsertNickname,
    isPending,
    error,
  } = useMutation({
    mutationFn: ({ nickname }: UpsertNicknameVariables) => {
      return apiHelper.postAuth(auth, API_ENDPOINT.HUB_USER_NICKNAME, {
        bodyParams: { nickname },
      });
    },
    onSuccess: (_data, variables) => {
      trackEvent(METRICS_EVENTS.CEDE_ARENA_USERNAME_SET, { via: variables.via });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.HUB_USER_ACCOUNTS] });
    },
  });

  return {
    nickname: hubUser?.nickname ?? null,
    upsertNickname,
    isPending,
    error,
  };
};
